import { useTranslation } from 'react-i18next';
import { useIsSplitLayout } from '@/hooks/useMediaQuery';
import {
  CalendarIcon,
  ExperienceIcon,
  FlagIcon,
  JobApplyActions,
  JobCardFooter,
  JobCardFrame,
  JobCardHead,
  JobFact,
  JobFacts,
  LocationIcon,
  SkillsIcon,
  jobCardStyles as styles,
} from '@/features/jobs/components/JobCardParts';
import { formatExperience, formatPostedDate } from '@/features/jobs/utils/jobHelpers';
import { useLongPressCopy } from '@/features/jobs/utils/longPressCopy';
import type { JobEntity } from '@/types/job.types';

/**
 * HomeJobListItem — job card in the Home / Jobs list/detail layout.
 * Shows the same data as Flutter WebJobDetailsCard: title + hot flame, relevance match,
 * save / share, Job ID, location, skills, experience, posted date and the apply actions.
 * Long-pressing the title or the Job ID copies it.
 *
 * With the desktop list/detail split, selecting the card fills the detail panel and applying
 * is done there, so the footer only shows the Applied status. Without the split (below 1024px),
 * or with `alwaysShowActions`, the footer carries Flutter's per-card Easy Apply / Apply /
 * Applied buttons (web_job_details_card.dart ~323-367) when `onApply` is given.
 */

interface HomeJobListItemProps {
  entity: JobEntity;
  isSelected: boolean;
  onSelect: () => void;
  onSave: () => void;
  onUnsave: () => void;
  /** Tenant config `enableEasyApply` — shows Easy Apply next to Apply */
  enableEasyApply?: boolean;
  /** Apply / Easy Apply from the card (Flutter WebJobDetailsCard onTap(isEasyApply)) */
  onApply?: (isEasyApply: boolean) => void;
  /** Show the apply actions at every width (pages without a detail panel, e.g. /savedJobs) */
  alwaysShowActions?: boolean;
}

export function jobMetaLine(entity: JobEntity): string {
  const location = entity.jobLocation?.[0]?.fqLocationName;
  const hasExperience =
    entity.requiredExperienceInMonths?.from != null || entity.requiredExperienceInMonths?.to != null;
  const experience = hasExperience
    ? formatExperience(entity.requiredExperienceInMonths?.from, entity.requiredExperienceInMonths?.to)
    : '';
  return [location, experience, entity.departmentName].filter(Boolean).join(' · ');
}

/** Flutter card field values ('N/A' when missing). */
export function jobCardFields(entity: JobEntity, t: (key: string) => string) {
  return {
    displayId: entity.displayId ?? 'N/A',
    location: entity.jobLocation?.[0]?.fqLocationName || 'N/A',
    skills: (entity.skills ?? []).map((s) => s.skill).filter(Boolean).join(', ') || 'N/A',
    experience: formatExperience(
      entity.requiredExperienceInMonths?.from,
      entity.requiredExperienceInMonths?.to,
    ),
    postedDate: formatPostedDate(entity.jobPosting?.startDate, t),
  };
}

export function HomeJobListItem({
  entity,
  isSelected,
  onSelect,
  onSave,
  onUnsave,
  enableEasyApply = false,
  onApply,
  alwaysShowActions = false,
}: HomeJobListItemProps) {
  const { t } = useTranslation();
  const isSplit = useIsSplitLayout();

  const { displayId, location, skills, experience, postedDate } = jobCardFields(entity, t);
  // List cards count Easy Apply too (WebJobDetailsCard: isApplied || isEasyApplied)
  const isApplied = !!(entity.isApplied || entity.isEasyApplied);
  const showActions = !!onApply && (alwaysShowActions || !isSplit);
  const longPressJobId = useLongPressCopy(displayId);
  const relevanceLevel =
    entity.relevance && 'level' in entity.relevance ? entity.relevance.level : undefined;

  return (
    <JobCardFrame isSelected={isSelected} onSelect={onSelect}>
      <JobCardHead
        title={entity.jobTitle}
        displayId={displayId}
        jobId={entity.id}
        isHot={entity.hotJob?.isHot ?? false}
        relevanceLevel={relevanceLevel}
        isSaved={entity.isSavedJob}
        isSaving={entity.isLoadingForSaveJobButton}
        onSave={onSave}
        onUnsave={onUnsave}
        copyTitleOnLongPress
      />

      <JobFacts>
        <JobFact icon={<FlagIcon />} label={t('Job ID')}>
          <span {...longPressJobId}>
            {t('Job ID')} {displayId}
          </span>
        </JobFact>
        <JobFact icon={<LocationIcon />} label={t('Location')}>
          {location}
        </JobFact>
        <JobFact icon={<ExperienceIcon />} label={t('Experience')}>
          {experience}
        </JobFact>
        <JobFact icon={<SkillsIcon />} label={t('Skills')} tooltip={skills} wide>
          {skills}
        </JobFact>
      </JobFacts>

      <JobCardFooter
        start={
          <span className={styles.posted} title={t('Posted on')}>
            <CalendarIcon />
            {t('Posted')} {postedDate}
          </span>
        }
      >
        {showActions && onApply ? (
          <JobApplyActions isApplied={isApplied} enableEasyApply={enableEasyApply} onApply={onApply} />
        ) : (
          isApplied && <span className={styles.appliedBadge}>{t('Applied')}</span>
        )}
      </JobCardFooter>
    </JobCardFrame>
  );
}
